import { SITE_URL } from '../constants';

export { SITE_URL };
export const SITE_NAME = 'Induce AI';

export const DEFAULT_TITLE = 'Induce AI — AI Filmmaking Platform | Script-to-Video with Continuity';
export const DEFAULT_DESCRIPTION =
  'Induce AI builds vision-language models for film reasoning. Trained by film craft experts, they understand story, emotion, continuity, and cinematic grammar.';

export const OG_IMAGE = 'https://distribution.getinduce.com/assets/landing_page/home.jpg';
export const ORG_LOGO = `${SITE_URL}/favicon.ico`;
export const ORG_ID = `${SITE_URL}/#organization`;

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

/**
 * Normalize an internal site path to the canonical URL path policy.
 *
 * The root URL intentionally keeps its trailing slash; every other route
 * uses no trailing slash. Query strings and fragments are not part of a
 * canonical URL and are therefore removed here as well.
 */
export function normalizeSitePath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '/';

  let pathname = trimmed;
  try {
    // Supporting absolute URLs makes this helper safe for data-driven routes
    // while keeping the returned value an internal path.
    pathname = new URL(trimmed, SITE_URL).pathname;
  } catch {
    pathname = trimmed.split(/[?#]/, 1)[0] || '/';
  }

  pathname = pathname.split(/[?#]/, 1)[0] || '/';
  if (!pathname.startsWith('/')) pathname = `/${pathname}`;
  pathname = pathname.replace(/\/{2,}/g, '/');
  if (pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
}

const OG_IMAGE_FILES: Record<string, string> = {
  '/rhapsody': 'rhapsody.jpg',
  '/product/script-editor': 'product-script-editor.jpg',
  '/product/ai-video-continuity': 'product-ai-video-continuity.jpg',
  '/product/shot-control': 'product-shot-control.jpg',
  '/product/models': 'product-models.jpg',
  '/for-filmmakers': 'for-filmmakers.jpg',
  '/for-agencies': 'for-agencies.jpg',
  '/for-enterprise': 'for-enterprise.jpg',
  '/for-creators': 'for-creators.jpg',
  '/pricing': 'pricing.jpg',
  '/blog': 'blog.jpg',
};

export function pageOgImage(path: string): string | undefined {
  const normalized = normalizeSitePath(path);
  const file = OG_IMAGE_FILES[normalized];
  return file ? `${SITE_URL}/og/${file}` : undefined;
}

export const LEGAL_TITLES = {
  privacy: 'Privacy Policy | Induce AI',
  terms: 'Terms of Service | Induce AI',
} as const;

export function pageUrl(path: string) {
  const normalized = normalizeSitePath(path);
  return normalized === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalized}`;
}

export type PageMeta = {
  title: string;
  description: string;
  path: string;
  robots?: string;
};

export const HOME_META: PageMeta = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  path: '/',
};

export const PRIVACY_META: PageMeta = {
  title: LEGAL_TITLES.privacy,
  description:
    'Induce AI Privacy Policy: how we collect, use, store, and protect personal data when you use inducelabs.ai and our services.',
  path: '/privacy-policy',
};

export const TERMS_META: PageMeta = {
  title: LEGAL_TITLES.terms,
  description:
    'Induce AI Terms of Service: the legal agreement governing access and use of the platform, API, and generated content.',
  path: '/terms-of-service',
};

export const RHAPSODY_META: PageMeta = {
  title: 'Rhapsody — Causal Reasoning + Verification Engine for AI Film | Induce AI',
  description:
    'Rhapsody is Induce AI’s orchestration engine. It plans shots using live story context, routes them to video models, and verifies each result against the story and film craft.',
  path: '/rhapsody',
};

export const WHAT_IS_INDUCE_META: PageMeta = {
  title: 'What Is Induce AI? Film Reasoning and AI Filmmaking',
  description:
    'Induce AI is story infrastructure for AI filmmaking. Its Rhapsody engine plans shots from your screenplay and verifies each one against story logic and film grammar.',
  path: '/what-is-induce',
};

export const PRODUCT_SCRIPT_META: PageMeta = {
  title: 'Script to Video AI — Upload a Screenplay, Get a Verified Cut | Induce AI',
  description:
    'Induce AI is a script-to-video platform built on Rhapsody, a film reasoning engine that plans each shot and verifies it against your story before it enters the cut.',
  path: '/product/script-editor',
};

export const PRODUCT_SHOT_CONTROL_META: PageMeta = {
  title: 'Director-Grade Shot Control for AI Video | Induce AI',
  description:
    'Override any AI-generated shot — angle, lens, lighting, pacing, performance — without re-rolling the cut. Every override is local, non-destructive, and re-verified against the story before it lands.',
  path: '/product/shot-control',
};

export const PRODUCT_MODELS_META: PageMeta = {
  title: 'AI Video Generation Models Orchestrated by Rhapsody | Induce AI',
  description:
    'Induce AI orchestrates AI video models inside Rhapsody. The models render; Rhapsody plans, routes, and verifies each shot against the story.',
  path: '/product/models',
};

export const PRICING_META: PageMeta = {
  title: 'Pricing | Induce AI',
  description:
    'Monthly credits for creators and teams. Custom usage agreements for Enterprise.',
  path: '/pricing',
};

export const FAQ_META: PageMeta = {
  title: 'FAQ | Induce AI',
  description:
    'Answers on script formats, continuity, shot control, API access, commercial rights, and how Induce AI differs from prompt-based AI video tools.',
  path: '/faq',
};

export const SHOWREEL_META: PageMeta = {
  title: 'Showreel | Induce AI',
  description:
    'Watch film cuts made with Induce AI — a library of Rhapsody Engine output across genres, formats, and use cases.',
  path: '/showreel',
};

export const FOR_FILMMAKERS_META: PageMeta = {
  title: 'AI Filmmaking Tools for Directors — Script to Cut | Induce AI',
  description:
    'Induce AI is a filmmaking platform for directors. Upload a screenplay and get a cinematic cut with consistent characters and control over every shot.',
  path: '/for-filmmakers',
};

export const FOR_AGENCIES_META: PageMeta = {
  title: 'AI Video Production for Agencies | From $15k/min to $600/min | Induce AI',
  description:
    'Induce AI takes agencies from brief to branded cinematic cut in one pipeline, with brand continuity across projects and commercial rights on every tier.',
  path: '/for-agencies',
};

export const FOR_ENTERPRISE_META: PageMeta = {
  title: 'Enterprise AI Video Infrastructure & Text-to-Video API | Induce AI',
  description:
    'Induce AI provides enterprise video infrastructure: a text-to-video API, private models, VPC deployment, SSO/SAML, RBAC, and audit logging.',
  path: '/for-enterprise',
};

export const FOR_CREATORS_META: PageMeta = {
  title: 'AI Video for Creators | Induce AI',
  description:
    'For indie filmmakers and solo creators shooting out of their own kit. Script-first editor, shot overrides, and community continuity models.',
  path: '/for-creators',
};

export const SCRIPT_TO_VIDEO_META: PageMeta = {
  title: 'Script to Video AI — Upload a Screenplay, Get a Cut | Induce AI',
  description:
    'Induce AI reads your screenplay like a director, evaluating structure, logic, and intent before generating each shot with continuity.',
  path: '/use-cases/script-to-video',
};

export const AI_VIDEO_CONTINUITY_META: PageMeta = {
  title: 'AI Video Continuity — Consistent Characters, Verified on Every Shot | Induce AI',
  description:
    'Keep characters, wardrobe, and locations consistent across AI-generated shots. Induce AI’s Project Memory Graph tracks continuity, while its Coherence Verifier checks every frame.',
  path: '/product/ai-video-continuity',
};

export const AI_PREVIZ_META: PageMeta = {
  title: 'AI Previz - AI Previsualization From Your Script | Induce AI',
  description:
    'Previsualize your film before you shoot. Induce AI turns a screenplay into a visual cut with shots, blocking, and continuity before the camera rolls.',
  path: '/use-cases/ai-previz',
};

export const BLOG_META: PageMeta = {
  title: 'Blog | Induce AI',
  description: 'Guides, tutorials, and insights on AI filmmaking from the Induce AI team.',
  path: '/blog',
};

export const NOT_FOUND_META: PageMeta = {
  title: 'Page not found | Induce AI',
  description:
    'This page does not exist. Head home, explore the product, or read the Induce AI blog.',
  path: '/404',
  robots: 'noindex, nofollow',
};

export type PrerenderHeadElement =
  | { type: 'meta'; props: Record<string, string> }
  | { type: 'link'; props: Record<string, string> }
  | { type: 'script'; props: { type: string }; children: string };

export type PageSeoOptions = {
  ogImage?: string;
  ogType?: 'website' | 'article';
  article?: { publishedTime: string; author: string };
  jsonLd?: Record<string, unknown>;
};

/** @deprecated Use PageSeoOptions */
export type PrerenderHeadOptions = PageSeoOptions & {
  extraElements?: PrerenderHeadElement[];
};

export function buildPageSeoDescriptors(meta: PageMeta, options: PageSeoOptions = {}) {
  const url = pageUrl(meta.path);
  const image = options.ogImage ?? pageOgImage(meta.path) ?? OG_IMAGE;
  const ogType = options.ogType ?? 'website';

  const elements: PrerenderHeadElement[] = [
    { type: 'meta', props: { name: 'description', content: meta.description } },
    ...(meta.robots
      ? [{ type: 'meta' as const, props: { name: 'robots', content: meta.robots } }]
      : []),
    { type: 'link', props: { rel: 'canonical', href: url } },
    { type: 'meta', props: { property: 'og:type', content: ogType } },
    { type: 'meta', props: { property: 'og:url', content: url } },
    { type: 'meta', props: { property: 'og:title', content: meta.title } },
    { type: 'meta', props: { property: 'og:description', content: meta.description } },
    { type: 'meta', props: { property: 'og:site_name', content: SITE_NAME } },
    { type: 'meta', props: { property: 'og:locale', content: 'en_US' } },
    { type: 'meta', props: { property: 'og:image', content: image } },
    { type: 'meta', props: { property: 'og:image:width', content: String(OG_IMAGE_WIDTH) } },
    { type: 'meta', props: { property: 'og:image:height', content: String(OG_IMAGE_HEIGHT) } },
    { type: 'meta', props: { property: 'og:image:alt', content: meta.title } },
    { type: 'meta', props: { name: 'twitter:card', content: 'summary_large_image' } },
    { type: 'meta', props: { name: 'twitter:title', content: meta.title } },
    { type: 'meta', props: { name: 'twitter:description', content: meta.description } },
    { type: 'meta', props: { name: 'twitter:image', content: image } },
    { type: 'meta', props: { name: 'twitter:image:alt', content: meta.title } },
  ];

  if (options.article) {
    elements.push(
      { type: 'meta', props: { property: 'article:published_time', content: options.article.publishedTime } },
      { type: 'meta', props: { property: 'article:author', content: options.article.author } },
    );
  }

  if (options.jsonLd) {
    elements.push({
      type: 'script',
      props: { type: 'application/ld+json' },
      children: JSON.stringify(options.jsonLd),
    });
  }

  return { title: meta.title, elements };
}

export function buildPrerenderHead(meta: PageMeta, options: PrerenderHeadOptions = {}) {
  const { extraElements, ...seoOptions } = options;
  const { title, elements } = buildPageSeoDescriptors(meta, seoOptions);

  return {
    title,
    elements: new Set([...elements, ...(extraElements ?? [])]),
  };
}
